import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import type { MenuCloseReason } from "components/common/DropdownMenu";
import { isTextEntry } from "@/lib/isTextEntry";

export interface ContextMenuPosition {
  x: number;
  y: number;
}

/** How long a touch has to be held to stand in for a right-click. */
export const LONG_PRESS_MS = 500;
/** Drift that reads as a scroll rather than a hold, and cancels the long press. */
const LONG_PRESS_SLOP_PX = 10;
/** Inset from the row's left edge when the keyboard opens the menu. */
const KEYBOARD_ANCHOR_INSET = 8;

interface ContextMenuTriggerProps {
  onContextMenu: (event: ReactMouseEvent) => void;
  onKeyDown: (event: ReactKeyboardEvent) => void;
  onPointerDown: (event: ReactPointerEvent) => void;
  onPointerMove: (event: ReactPointerEvent) => void;
  onPointerUp: () => void;
  onPointerCancel: () => void;
  onClickCapture: (event: ReactMouseEvent) => void;
}

export interface RowContextMenu {
  position: ContextMenuPosition | null;
  /// The portaled menu takes the pointer off the row, so `:hover` drops — rows
  /// pin their hover styling and hover-revealed actions on this.
  isOpen: boolean;
  openAt: (position: ContextMenuPosition) => void;
  close: (reason?: MenuCloseReason) => void;
  /// Spread onto the element the menu belongs to. Pair with `tabIndex={0}` on
  /// rows where the menu is the only path to some of their actions.
  triggerProps: ContextMenuTriggerProps;
}

/// The open menu is a React portal, so its events still travel the React tree
/// through the row that owns it — but its DOM node is not inside the row.
const isOwnEvent = (event: {
  currentTarget: EventTarget | null;
  target: EventTarget | null;
}): boolean =>
  (event.currentTarget as HTMLElement | null)?.contains(
    event.target as Node | null,
  ) ?? false;

interface UseContextMenuOptions {
  /// Runs before every open, whatever triggered it. The directory uses it to
  /// select the row first, since the details panel runs the panel-owned items.
  onBeforeOpen?: () => void;
}

/**
 * Position state plus the three ways a row context menu opens: right-click,
 * `Shift+F10` / the Menu key, and a touch hold.
 */
export const useContextMenu = ({
  onBeforeOpen,
}: UseContextMenuOptions = {}): RowContextMenu => {
  const [position, setPosition] = useState<ContextMenuPosition | null>(null);
  /// Set only for a keyboard open: after an outside click the browser has
  /// already moved focus where the user pointed, so there is nothing to give back.
  const restoreFocusTo = useRef<HTMLElement | null>(null);
  const holdTimer = useRef<number | null>(null);
  const holdOrigin = useRef<ContextMenuPosition | null>(null);
  /// A hold that opened the menu still ends in a click — which would run the
  /// row's primary action on top of it.
  const swallowNextClick = useRef(false);

  const cancelHold = useCallback(() => {
    if (holdTimer.current !== null) window.clearTimeout(holdTimer.current);
    holdTimer.current = null;
    holdOrigin.current = null;
  }, []);

  useEffect(() => cancelHold, [cancelHold]);

  const openAt = useCallback(
    (next: ContextMenuPosition) => {
      onBeforeOpen?.();
      setPosition(next);
    },
    [onBeforeOpen],
  );

  const close = useCallback((reason?: MenuCloseReason) => {
    setPosition(null);
    if (reason !== "outside") restoreFocusTo.current?.focus();
    restoreFocusTo.current = null;
  }, []);

  const onContextMenu = useCallback(
    (event: ReactMouseEvent) => {
      if (!isOwnEvent(event)) return;
      event.preventDefault();
      event.stopPropagation();
      cancelHold();
      restoreFocusTo.current = null;
      openAt({ x: event.clientX, y: event.clientY });
    },
    [cancelHold, openAt],
  );

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent) => {
      const isMenuKey =
        event.key === "ContextMenu" || (event.shiftKey && event.key === "F10");
      if (!isMenuKey || isTextEntry(event.target) || !isOwnEvent(event)) return;
      event.preventDefault();
      event.stopPropagation();
      const row = event.currentTarget as HTMLElement;
      const rect = row.getBoundingClientRect();
      restoreFocusTo.current = row;
      openAt({ x: rect.left + KEYBOARD_ANCHOR_INSET, y: rect.bottom });
    },
    [openAt],
  );

  const onPointerDown = useCallback(
    (event: ReactPointerEvent) => {
      if (event.pointerType !== "touch" || !isOwnEvent(event)) return;
      // A hold that opened the menu with no click after it must not eat the next tap.
      swallowNextClick.current = false;
      const { clientX: x, clientY: y } = event;
      holdOrigin.current = { x, y };
      holdTimer.current = window.setTimeout(() => {
        holdTimer.current = null;
        holdOrigin.current = null;
        swallowNextClick.current = true;
        restoreFocusTo.current = null;
        openAt({ x, y });
      }, LONG_PRESS_MS);
    },
    [openAt],
  );

  const onPointerMove = useCallback(
    (event: ReactPointerEvent) => {
      const origin = holdOrigin.current;
      if (!origin) return;
      if (
        Math.abs(event.clientX - origin.x) > LONG_PRESS_SLOP_PX ||
        Math.abs(event.clientY - origin.y) > LONG_PRESS_SLOP_PX
      ) {
        cancelHold();
      }
    },
    [cancelHold],
  );

  const onClickCapture = useCallback((event: ReactMouseEvent) => {
    if (!swallowNextClick.current) return;
    swallowNextClick.current = false;
    if (!isOwnEvent(event)) return;
    event.preventDefault();
    event.stopPropagation();
  }, []);

  return {
    position,
    isOpen: position !== null,
    openAt,
    close,
    triggerProps: {
      onContextMenu,
      onKeyDown,
      onPointerDown,
      onPointerMove,
      onPointerUp: cancelHold,
      onPointerCancel: cancelHold,
      onClickCapture,
    },
  };
};
